import type { ReactNode } from 'react'
import type { Sample } from '../api'
import { inputPoint } from '../live-ui/adapter'
import { wavePaths } from '../live-ui/quality'

/*
 * The last 5 minutes of an input on the admin Streams page (owner, 18.10): bitrate (the line
 * takes the health of each 5 s window, as the Studio's wave), frame rate against what the
 * encoder declares (dashed), and the share of declared frames that did not arrive (bars).
 * GET /api/admin/streams/history, 60 samples of 5 s; fewer are drawn at the right edge (now).
 * Fixed sizes and mono figures: the 5 s updates do not move the row. Every number is also in
 * words next to its chart, so nothing depends on the colour.
 */

const W = 120
const H = 28
const SLOTS = 60

const x = (i: number, n: number) => ((i + Math.max(0, SLOTS - n)) * W) / (SLOTS - 1)
const live = (s: Sample) => s.state === 'live' && s.health !== 'offline'

function Mini({ label, value, sub, title, children, testId }: { label: string; value: string; sub?: string; title: string; children: ReactNode; testId: string }) {
  return (
    <figure className="m-0 flex w-[132px] flex-none flex-col gap-1" data-testid={testId} title={title}>
      <figcaption className="flex items-baseline justify-between gap-2">
        <span className="mono-label uppercase">{label}</span>
        <span className="tabular font-mono text-[11px] whitespace-nowrap text-text-soft">{value}</span>
      </figcaption>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title} className="block rounded-sm bg-bg">
        {children}
      </svg>
      {sub && <span className="tabular truncate font-mono text-[10px] text-muted">{sub}</span>}
    </figure>
  )
}

export function StreamHistory({ name, samples, declaredFps }: { name: string; samples: Sample[]; declaredFps?: number }) {
  const n = samples.length
  const last = samples[n - 1]
  const on = samples.filter(live)

  // bitrate: the Studio's wave paths (health per window)
  const wave = wavePaths(samples.map(inputPoint), W, H)
  const mbps = last && live(last) ? `${(last.bitrate_kbps / 1000).toFixed(1)} Mb/s` : '—'

  // frame rate
  const fpsTop = Math.max(1, declaredFps ?? 0, ...samples.map((s) => s.fps ?? 0)) * 1.15
  const fy = (v: number) => H - 3 - (Math.min(v, fpsTop) / fpsTop) * (H - 7)
  let fpsPath = ''
  for (let i = 1; i < n; i++) {
    const a = samples[i - 1]!, b = samples[i]!
    if (!live(a) || !live(b)) continue
    fpsPath += `M${x(i - 1, n).toFixed(1)} ${fy(a.fps ?? 0).toFixed(1)}L${x(i, n).toFixed(1)} ${fy(b.fps ?? 0).toFixed(1)}`
  }
  const fpsMin = on.length ? Math.min(...on.map((s) => s.fps ?? 0)) : undefined
  const fpsNow = last && live(last) && last.fps !== undefined ? `${last.fps.toFixed(0)} fps` : '—'

  // dropped frames: bars, scale at least 5 %
  const drops = samples.map((s) => (live(s) ? (s.drop_pct ?? 0) : -1))
  const dropTop = Math.max(5, ...drops)
  const bw = Math.max(1, W / SLOTS - 0.6)
  const worst = Math.max(0, ...drops)
  const dropNow = last && live(last) && last.drop_pct !== undefined ? `${last.drop_pct.toFixed(1)} %` : '—'
  const pkts = on.reduce((a, s) => a + (s.pkt_drop ?? 0), 0)
  const srt = on.some((s) => s.pkt_drop !== undefined)

  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1" data-testid={`stream-history-${name}`} data-samples={n}>
      <Mini label="Bitrate" value={mbps} title={`Bitrate, last 5 minutes${last ? `, now ${mbps}` : ''}; the line takes the health of each 5 s window`} testId={`stream-history-bitrate-${name}`}>
        <path d={wave.idle} className="stroke-slot" strokeDasharray="2 3" fill="none" />
        <path d={wave.good} className="stroke-live" strokeWidth="1.4" fill="none" />
        <path d={wave.warn} className="stroke-warn" strokeWidth="1.8" fill="none" />
        <path d={wave.bad} className="stroke-fail" strokeWidth="1.8" fill="none" />
      </Mini>
      <Mini
        label="Frames"
        value={fpsNow}
        sub={declaredFps ? `declared ${declaredFps.toFixed(0)}${fpsMin !== undefined ? ` · min ${fpsMin.toFixed(0)}` : ''}` : fpsMin !== undefined ? `min ${fpsMin.toFixed(0)}` : undefined}
        title={`Frame rate, last 5 minutes${declaredFps ? ` (dashed: ${declaredFps.toFixed(0)} declared by the encoder)` : ''}`}
        testId={`stream-history-fps-${name}`}
      >
        {!!declaredFps && <path d={`M0 ${fy(declaredFps).toFixed(1)}H${W}`} className="stroke-line-2" strokeDasharray="2 3" fill="none" />}
        <path d={fpsPath} className="stroke-text-soft" strokeWidth="1.4" fill="none" />
      </Mini>
      <Mini
        label="Dropped"
        value={dropNow}
        sub={`max ${worst.toFixed(1)} %${srt ? ` · SRT ${pkts} pkt` : ''}`}
        title={`Frames that did not arrive, percent of the declared rate, per 5 s window${srt ? `; the SRT receiver dropped ${pkts} late packets in 5 minutes` : ''}`}
        testId={`stream-history-drop-${name}`}
      >
        <path d={`M0 ${H - 2.5}H${W}`} className="stroke-line" fill="none" />
        {drops.map((d, i) =>
          d > 0 ? (
            <rect key={i} x={x(i, n) - bw / 2} y={H - 3 - (Math.min(d, dropTop) / dropTop) * (H - 6)} width={bw} height={(Math.min(d, dropTop) / dropTop) * (H - 6)} className={d >= 5 ? 'fill-fail' : 'fill-warn'} />
          ) : null,
        )}
      </Mini>
    </div>
  )
}
